import { cn } from "cn";
import type * as React from "react";
import { IconButton, type IconButtonProps } from "./icon-button";

// Inline SVG to avoid icon dependency
function CloseIcon({ size = 20 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M18 6L6 18M6 6l12 12" />
    </svg>
  );
}

type CloseButtonColorScheme = "light" | "dark";

export type CloseButtonProps = Omit<
  IconButtonProps,
  "aria-label" | "children" | "kind"
> & {
  /** Visual scheme: "light" for dark backgrounds, "dark" for light backgrounds */
  colorScheme?: CloseButtonColorScheme;
  /** Icon size in pixels */
  iconSize?: number;
};

const colorSchemeClasses: Record<CloseButtonColorScheme, string> = {
  light:
    "text-text-light hover:bg-bg-light/30 dark:text-text-light dark:hover:bg-bg-light/10",
  dark: "text-text-dark hover:bg-bg-dark/30 dark:text-text-dark dark:hover:bg-bg-dark/10",
};

export function CloseButton({
  size = "md",
  colorScheme = "dark",
  iconSize,
  className,
  ...props
}: CloseButtonProps) {
  const defaultIconSizes: Record<string, number> = {
    xs: 16,
    sm: 18,
    md: 20,
  };

  const resolvedIconSize = iconSize ?? defaultIconSizes[size] ?? 18;

  return (
    <IconButton
      kind="ghost"
      size={size}
      aria-label="Close"
      className={cn(colorSchemeClasses[colorScheme], className)}
      {...props}
    >
      <CloseIcon size={resolvedIconSize} />
    </IconButton>
  );
}
